<template>
  <div
    v-if="filterChips.length"
    class="d-flex align-center pb-5 gap-1 overflow-x-auto"
    data-testid="so-data-table-filters"
  >
    <SoChip
      v-for="filter in filterChips"
      :key="`${filter.name}-${filter.option}`"
      :text="filter.label"
      :type="SoChipType.FILTER"
      data-testid="so-data-table-filter-chip"
      size="x-large"
      @close="removeFilter(filter.name, filter.option)"
    />
  </div>
</template>

<script lang="ts">
  import {
    FilterType,
    SoDataTableFilters,
  } from "@/types/components/SoDataTable"
  import { PropType } from "vue"
  import SoChip from "../SoChip.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import { formatDate, formatDateTime } from "@/helpers/dateHelper"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import dayjs from "dayjs"
  import { cloneDeep } from "lodash-es"

  export default {
    components: { SoChip },
    props: {
      filters: {
        type: Object as PropType<SoDataTableFilters>,
        default: () => ({}),
      },
    },
    emits: ["update:filters"],
    data() {
      return {
        FilterType,
        SoChipType,
      }
    },
    computed: {
      filterChips() {
        const chips: {
          name: string
          label: string
          option: string | number
        }[] = []

        for (const name in this.filters) {
          const filter = this.filters[name]

          // RADIO
          if (filter.type === FilterType.RADIO && filter.value) {
            const optLabel = filter.options[filter.value]
            const label = `${filter.label}: ${optLabel}`
            chips.push({ name, label, option: filter.value })
          }

          // CHECKBOX
          else if (filter.type === FilterType.CHECKBOX && filter.value) {
            filter.value.forEach((option) => {
              const optLabel = filter.options[option]
              const label = `${filter.label}: ${optLabel}`
              chips.push({ name, label, option })
            })
          }

          // DATE_RANGE
          else if (filter.type === FilterType.DATE_RANGE) {
            if (filter.value.from) {
              const date = formatDate(dayjs(filter.value.from))
              const label = `${filter.label} From: ${date}`
              chips.push({ name, label, option: "from" })
            }
            if (filter.value.to) {
              const date = formatDate(dayjs(filter.value.to))
              const label = `${filter.label} To: ${date}`
              chips.push({ name, label, option: "to" })
            }
          }

          // DATE_TIME_RANGE
          else if (filter.type === FilterType.DATE_TIME_RANGE) {
            if (filter.value) {
              const label = filter.value
                .map((dt) => formatDateTime(dayjs(dt)))
                .join(" - ")
              chips.push({ name, label, option: label })
            }
          }

          // MONEY_RANGE
          else if (filter.type === FilterType.MONEY_RANGE) {
            if (filter.value.min) {
              const amount = formatMoney({ amount: filter.value.min })
              const label = `${filter.label} Min: ${amount}`
              chips.push({ name, label, option: "min" })
            }
            if (filter.value.max) {
              const amount = formatMoney({ amount: filter.value.max })
              const label = `${filter.label} Max: ${amount}`
              chips.push({ name, label, option: "max" })
            }
          }

          // NUMBER_RANGE
          else if (filter.type === FilterType.NUMBER_RANGE) {
            if (filter.value.min) {
              const value = filter.value.min
              const label = `${filter.label} Min: ${value}`
              chips.push({ name, label, option: "min" })
            }
            if (filter.value.max) {
              const value = filter.value.max
              const label = `${filter.label} Max: ${value}`
              chips.push({ name, label, option: "max" })
            }
          }
        }
        return chips
      },
    },
    methods: {
      removeFilter(filterName: string, option: string | number) {
        const filters = cloneDeep(this.filters)
        const filter = filters[filterName]

        if (!filter) return
        if (
          filter.type === FilterType.RADIO ||
          filter.type === FilterType.DATE_TIME_RANGE
        ) {
          filter.value = null
        } else if (filter.type === FilterType.CHECKBOX) {
          filter.value = filter.value.filter((opt) => opt !== option)
        } else if (
          filter.type === FilterType.DATE_RANGE ||
          filter.type === FilterType.MONEY_RANGE ||
          filter.type === FilterType.NUMBER_RANGE
        ) {
          filter.value[option] = null
        }

        this.$emit("update:filters", filters)
      },
    },
  }
</script>
